import { Controller } from '@hotwired/stimulus';
import displayFlash from '../utils/displayFlash';

interface CalendlyProps {
  url: string;
  parentElement: Element;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prefill: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  utm: any;
}

type CalendlyApi = {
  initInlineWidget: (props: CalendlyProps) => void;
};

type CalendlyMessageEvent = MessageEvent<{ event: string }>;

/** Avoid augmenting Window.Calendly (conflicts with other declarations / modifiers). */
function getCalendlyApi(): CalendlyApi | undefined {
  return (window as unknown as { Calendly?: CalendlyApi }).Calendly;
}

function initTimeout() {
  window.setTimeout(() => {
    window.location.replace('/home');
  }, 1000 * 60 * 5);
}

function isCalendlyEvent(e: CalendlyMessageEvent) {
  return e.origin === 'https://calendly.com' && e.data.event && e.data.event.indexOf('calendly.') === 0;
}

function calendlyHandler(e: CalendlyMessageEvent) {
  if (!isCalendlyEvent(e)) return;
  if (e.data.event !== 'calendly.event_scheduled') return;

  document.getElementById('back-to-coaches')?.classList.add('invisible');
  displayFlash('Your session has been confirmed.', 8000);
  window.location.href = '/home';
}

export default class extends Controller {
  /* eslint-disable max-len */
  static targets = ['embed', 'managerDialog'];

  static values = {
    coachCalendlyUrl: String,
    userFullName: String,
    userEmailAddress: String,
    userOrganisation: String,
    coachName: String,
    campaign: String,
    managerEmail: String,
    managerName: String,
  };

  declare embedTarget: HTMLElement;
  declare managerDialogTarget: HTMLDialogElement;
  declare hasManagerDialogTarget: boolean;
  declare userFullNameValue: string;
  declare userEmailAddressValue: string;
  declare coachCalendlyUrlValue: string;
  declare userOrganisationValue: string;
  declare coachNameValue: string;
  declare campaignValue: string;
  declare managerEmailValue: string;
  declare managerNameValue: string;
  declare boundCalendlyHandler: (e: CalendlyMessageEvent) => void;

  private includeManagerGuest = false;
  private calendlyStarted = false;

  connect() {
    this.boundCalendlyHandler = calendlyHandler;
    window.addEventListener('message', this.boundCalendlyHandler);
    initTimeout();

    if (this.managerEmailValue && this.hasManagerDialogTarget) {
      this.managerDialogTarget.showModal();
    } else {
      this.startCalendly();
    }
  }

  disconnect() {
    window.removeEventListener('message', this.boundCalendlyHandler);
  }

  inviteManager() {
    this.includeManagerGuest = true;
    this.managerDialogTarget.close();
    this.startCalendly();
  }

  bookWithoutManager() {
    this.includeManagerGuest = false;
    this.managerDialogTarget.close();
    this.startCalendly();
  }

  startCalendly() {
    if (this.calendlyStarted) return;
    this.calendlyStarted = true;
    this.loadCalendlyScript();
  }

  loadCalendlyScript() {
    if (getCalendlyApi()) {
      this.initCalendlyWidget();
    } else {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.onload = this.initCalendlyWidget.bind(this);

      document.head.appendChild(script);
    }
  }

  initCalendlyWidget() {
    getCalendlyApi()?.initInlineWidget({
      url: this.coachCalendlyUrl(),
      parentElement: this.embedTarget,
      prefill: {
        name: this.userFullNameValue,
        email: this.userEmailAddressValue,
        customAnswers: {
          a1: this.userOrganisationValue,
        },
      },
      utm: {
        utmCampaign: this.campaignValue,
        utmSource: `SanctusBookingSystem-${process.env.RAILS_ENV}`,
        utmContent: this.userEmailAddressValue,
        utmMedium: 'CalendarPage',
      },
    });
  }

  coachCalendlyUrl() {
    let url = `${this.coachCalendlyUrlValue}?hide_gdpr_banner=1`;
    if (this.includeManagerGuest && this.managerEmailValue) {
      url += `&guests=${encodeURIComponent(this.managerEmailValue)}`;
    }
    return url;
  }
}
